import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useCounts } from '../hooks/useCounts'
import { useVersionCheck } from '../hooks/useVersionCheck'
import { APP_VERSION, SHORT_VERSION } from '../lib/version'

/**
 * The top navigation bar + main area — same structure and classes as the prototype's
 * sticky nav (not the sibling app's sidebar; OIP's five tabs fit a bar, and the demo's
 * visual identity is the bar). Two things changed from the prototype:
 *  - the tabs are real routes, so the URL survives a refresh and links can be shared;
 *  - the right side shows who is signed in and a sign-out button. The prototype's
 *    "Paste & scan" button there duplicated the + Scan tab, so it made way.
 * The nav also shows the build version, and a banner appears when the deployed frontend
 * has moved on from the bundle this tab is running — both born of a support case where a
 * stale tab reported the previous release as a bug.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { me, signOut } = useAuth()
  const { hot, pipeline } = useCounts()
  const stale = useVersionCheck()

  const tab = ({ isActive }: { isActive: boolean }) => `nav-tab${isActive ? ' active' : ''}`

  return (
    <>
      <nav className="nav">
        <div className="nav-logo">
          Burendo <span className="oip">OIP</span>
        </div>
        <NavLink to="/opportunities" className={tab}>
          Opportunities <span className="nav-badge">{hot}</span>
        </NavLink>
        <NavLink to="/pipeline" className={tab}>
          Pipeline <span className="nav-badge">{pipeline}</span>
        </NavLink>
        <NavLink to="/dcfh2" className={tab}>
          DCFH2 Pipeline
        </NavLink>
        <NavLink to="/awards" className={tab}>
          Awards
        </NavLink>
        <NavLink to="/scan" className={tab}>
          + Scan
        </NavLink>
        <div className="nav-right">
          <span className="nav-user" title={me?.user?.email ?? ''}>
            {me?.user?.displayName}
          </span>
          <button className="btn-scan" onClick={signOut}>
            Sign out
          </button>
          <span className="nav-version" title={`build ${APP_VERSION}`}>
            {SHORT_VERSION}
          </span>
        </div>
      </nav>
      {stale && (
        <div className="update-banner" role="status">
          A newer version of OIP has been deployed — this tab is still on {SHORT_VERSION}.{' '}
          <button onClick={() => window.location.reload()}>Refresh now</button>
        </div>
      )}
      {children}
    </>
  )
}
